import { useMemo, useState } from 'react';
import { Download, FileText, Search as SearchIcon, ShoppingCart } from '@/components/icons';
import { useQuery } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/form/Select';
import { Input } from '@/components/form/Input';
import { listPackings, packingExportPath, type Packing } from '@/api/packing';
import { downloadExcel } from '@/lib/downloadExcel';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { formatDate } from '@/lib/format';
import { AssortedPackingModal } from './AssortedPackingModal';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { orderRef, trackByOrderId } from '@/lib/orderRef';

const STATUS_TONE: Record<string, 'accent' | 'success' | 'muted' | 'danger'> = {
  Packing: 'accent',
  Verified: 'success',
  'GRN-Out': 'success',
  Rejected: 'danger',
};

const CHANNELS: [string, string][] = [['', 'All'], ['online', 'Online'], ['regular', 'Regular']];

export function PackingPage() {
  const [status, setStatus] = useState('');
  const [channel, setChannel] = useState('');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const docViewer = useDocViewer();

  // Pack date — All / Date (from–to) / Month / Quarter / Year.
  const period = usePeriod();
  const { range } = period;
  const filter = useMemo(
    () => ({ status: status || undefined, channel: channel || undefined, from: range?.from, to: range?.to }),
    [status, channel, range],
  );

  const query = useQuery({
    queryKey: ['packing', filter],
    queryFn: () => listPackings(filter),
    refetchInterval: 60_000,
  });
  const data = query.data ?? [];

  const toast = useToast();
  // Export every matching row (server-side, no cap) — the table shows the latest 200.
  function onExport() {
    downloadExcel(packingExportPath({ ...filter, search: search.trim() || undefined }), 'GRHAMOY_Packing.xlsx', 120_000)
      .catch((err) => toast.push(apiError(err, 'Export failed'), 'error'));
  }

  return (
    <div>
      <SectionHeader
        title="Packing"      />

      <div className="mb-3 flex gap-1">
        {CHANNELS.map(([v, label]) => (
          <button
            key={v}
            onClick={() => setChannel(v)}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors sm:px-3 sm:py-1.5 sm:text-sm ${channel === v ? 'bg-accent text-white' : 'bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'}`}
          >
            {label === 'Online' ? <><ShoppingCart size={12} className="mr-1 inline-block align-[-2px]" /> Online</> : label}
          </button>
        ))}
      </div>

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto">
          <div className="relative w-full">
            <SearchIcon
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[260px]"
              aria-label="Search packings"
            />
          </div>
        </FilterField>
        <FilterField label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option>Packing</option>
            <option>Verified</option>
            <option>GRN-Out</option>
            <option>Rejected</option>
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        <button
          type="button"
          onClick={onExport}
          className="col-span-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:col-span-1 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
          title="Export every packing matching these filters to Excel"
        >
          <Download size={12} /> Export
        </button>
      </FilterBar>

      <Table<Packing>
        storageKey="packing"
        paginated
        title="Packings"
        count={`${data.length} records`}
        rows={data}
        search={search}
        rowKey={(r) => r.id}
        loading={query.isFetching && data.length === 0}
        columns={trackByOrderId(channel === 'online', { key: 'orderId', header: 'Order ID', cell: (r) => <span className="font-extrabold text-accent">{orderRef(r.pickList?.dispatchOrder?.enquiry)}</span> }, ['packNo','pick','do'], [
          { key: 'packNo', header: 'PACK #', cell: (r) => <span className="font-extrabold text-accent">{r.packNo}</span> },
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.packDate) },
          { key: 'pick', header: 'Pick Ref', cell: (r) => r.pickList?.pickNo ?? '—' },
          { key: 'do', header: 'DO Ref', cell: (r) => r.pickList?.dispatchOrder?.doNo ?? '—' },
          { key: 'channel', header: 'Channel', align: 'center', cell: (r) => (r.pickList?.dispatchOrder?.enquiry?.source === 'Shopify' ? <Badge tone="info"><ShoppingCart size={12} className="mr-1 inline-block align-[-2px]" /> Online</Badge> : <span className="text-[var(--text-muted)]">Regular</span>) },
          { key: 'party', header: 'Customer', cell: (r) => <span className="font-bold">{r.onlineCustomer ? `${r.onlineCustomer} (Shopify)` : r.pickList?.dispatchOrder?.party?.name ?? '—'}</span> },
          { key: 'items', header: 'Items', align: 'right', cell: (r) => r.items.length },
          { key: 'qty', header: 'Qty', align: 'right', cell: (r) => r.items.reduce((sum, it) => sum + Number(it.doQty), 0) },
          { key: 'boxes', header: 'Boxes', align: 'right', cell: (r) => r.boxCount },
          { key: 'weight', header: 'Weight (kg)', align: 'right', cell: (r) => r.totalWeightKg },
          { key: 'dims', header: 'L×W×H (cm)', cell: (r) => {
            const dims = r.boxes
              .map((b) => (b.lengthCm && b.widthCm && b.heightCm ? `${+b.lengthCm}×${+b.widthCm}×${+b.heightCm}` : null))
              .filter(Boolean);
            return dims.length ? <span className="font-mono text-xs">{dims.join(', ')}</span> : <span className="text-[var(--text-muted)]">—</span>;
          } },
          {
            key: 'confirms',
            header: 'Confirm',
            cell: (r) => {
              const done = r.supervisorConfirmed && r.managerConfirmed;
              return (
                <span
                  className={`inline-block rounded-md px-2 py-0.5 text-[11px] font-bold ${
                    done
                      ? 'bg-success/15 text-success'
                      : 'bg-warning/15 text-warning'
                  }`}
                >
                  {done ? 'Confirmed' : 'Pending'}
                </span>
              );
            },
          },
          {
            key: 'status',
            header: 'Status',
            cell: (r) => <Badge tone={STATUS_TONE[r.status] ?? 'muted'}>{r.status}</Badge>,
          },
          {
            key: 'actions',
            header: '',
            width: '160px',
            cell: (r) => (
              <div className="flex gap-1.5">
                <Button variant="primary" size="sm" onClick={() => setOpenId(r.id)}>
                  {r.supervisorConfirmed ? 'View' : 'Open'}
                </Button>
                <Button
                  variant="glass"
                  size="sm"
                  onClick={() => docViewer.open('packing-scan-receipt', r.id, r.pickList?.dispatchOrder?.enquiry?.source === 'Shopify' ? orderRef(r.pickList.dispatchOrder.enquiry) : r.packNo)}
                  title="Open A4 document"
                >
                  <FileText size={14} />
                </Button>
              </div>
            ),
          },
        ])}
      />

      <AssortedPackingModal open={openId !== null} packingId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
